'use client'

import {
  ArticleGridItemFragment,
  Enum_Article_Category,
  GetFilteredSortedArticlesQueryVariables,
  GetTotalFilteredArticlesQueryVariables
} from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { Grid, gridItemTransition, GridType } from '@components/grid'
import { CommonFilterPanel } from '@components/grid/filter-panel'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { capitalizeFirstLetter } from '@utils/common'
import { articleRoute } from '@utils/routes'
import { BodyText, DetailText } from 'design-configuration/components'
import { motion } from 'motion/react'
import React from 'react'
import useMeasure from 'react-use-measure'
import { useArticlesSortAndFilter } from './use-articles-filters'
import { ValidArticleSorts } from './utils'

export const ArticleGridItem = ({ item }: { item: ArticleGridItemFragment | null }): React.ReactElement => {
  const [opacity, setOpacity] = React.useState(0)

  const [frameRef, frameBounds] = useMeasure()
  const frameWidth = frameBounds.width
  const frameHeight = frameWidth / 1.77
  if (!item) {
    return <></>
  }

  return (
    <AllPurposeLink href={articleRoute(item.slug)}>
      <motion.div
        key={item.slug}
        id={item.slug}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={gridItemTransition}
      >
        <div className="h-full w-full" ref={frameRef}>
          <div
            className="relative"
            style={{
              width: `${frameWidth}px`,
              height: `${frameHeight}px`
            }}
          >
            <motion.div className="h-full w-full" transition={{ duration: 0.3 }} animate={{ opacity: opacity }}>
              <ImageWithCustomLoader
                onLoad={() => setOpacity(1)}
                fill
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center'
                }}
                alt={`Article thumbnail for: ${item.title}`}
                sizes="33vw"
                src={item?.thumbnail?.data?.attributes?.url ?? ''}
              />
            </motion.div>
          </div>
        </div>
        <DetailText className="text-on-surface-variant mb-2xs mt-xs font-medium">
          {item.category?.toUpperCase()}
        </DetailText>
        <BodyText className="text-left">{item.title}</BodyText>
      </motion.div>
    </AllPurposeLink>
  )
}
export const ArticleGrid = ({
  initialGetTotalFilteredArticlesQueryVariables,
  initialGetFilteredSortedArticlesQueryVariables
}: {
  initialGetTotalFilteredArticlesQueryVariables: GetTotalFilteredArticlesQueryVariables
  initialGetFilteredSortedArticlesQueryVariables: GetFilteredSortedArticlesQueryVariables
}): React.ReactElement => {
  const {
    sortOrder,
    setPageNumber,
    setSortOrder,
    pageNumber,
    totalPages,
    pageData,
    totalItems,
    categoryFilterValue,
    setCategoryFilterValue,
    clearFilters
  } = useArticlesSortAndFilter({
    initialGetTotalFilteredArticlesQueryVariables,
    initialGetFilteredSortedArticlesQueryVariables
  })

  return (
    <Grid
      name={'articles'}
      gridType={GridType.Articles}
      itemNames="articles"
      sortOrder={sortOrder}
      sortOptions={Object.values(ValidArticleSorts)}
      onSortOrderChange={setSortOrder}
      onPageNumberChange={setPageNumber}
      pageNumber={pageNumber}
      totalPages={totalPages}
      numberOfActiveFilters={categoryFilterValue.length}
      onClearFilters={clearFilters}
      totalItems={totalItems}
      gridItems={pageData?.articles?.data ?? []}
      gridItemComponent={(item) => <ArticleGridItem item={item.attributes ?? null} />}
      filterLayout={
        <CommonFilterPanel
          filters={[
            {
              name: 'Article type',
              selectedOptions: categoryFilterValue,
              options: Object.values(Enum_Article_Category).map((v) => {
                return { label: capitalizeFirstLetter(v), value: v }
              }),
              onChange: (newCategoryFilterValue: string, selected: boolean) => {
                setCategoryFilterValue(newCategoryFilterValue, selected)
              }
            }
          ]}
        />
      }
    />
  )
}
